import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ChevronLeft, ChevronRight, Plus, Trash2, Save, Loader2 } from "lucide-react";
import type { Slide } from "./PresentationTemplates";
import { ExportButtons } from "./ExportButtons";
import { useLocalization } from '@/hooks/useLocalization';

interface DeckForEditor {
  id: string;
  name: string;
  slides: Slide[];
}

interface SlideEditorProps {
  deck: DeckForEditor;
  onClose: () => void;
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?([+-]\d{2}:\d{2}|Z)?)?$/;

// Formats a raw value for display in a flattened bullet - in particular,
// turns a raw ISO timestamp ("2025-01-25T00:00:00+00:00") into something
// readable ("Jan 25, 2025") instead of dumping the wire format verbatim.
function formatBulletValue(val: unknown): string {
  if (typeof val === "string" && ISO_DATE_RE.test(val)) {
    const d = new Date(val);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    }
  }
  return String(val);
}

// Flattens an arbitrary value into bullet strings. Defends against slides
// that don't match the {title, bullets, notes} contract - e.g. a
// malformed AI response that used a different field name/shape (seen live:
// {title, content: object|array}) - so a bad row can't crash the editor.
function toBulletStrings(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap((item) => toBulletStrings(item));
  }
  if (value && typeof value === "object") {
    return Object.entries(value as Record<string, unknown>).map(
      ([key, val]) => `${key.replace(/_/g, " ")}: ${formatBulletValue(val)}`
    );
  }
  if (typeof value === "string" && value.length > 0) {
    return [value];
  }
  return [];
}

function normalizeSlide(raw: unknown): Slide {
  const s = (raw ?? {}) as Record<string, unknown>;
  const title = typeof s.title === "string" && s.title.length > 0 ? s.title : "Untitled Slide";
  const bullets = Array.isArray(s.bullets) && s.bullets.every((b) => typeof b === "string")
    ? (s.bullets as string[])
    : toBulletStrings(s.content ?? s.bullets);
  const notes = typeof s.notes === "string" ? s.notes : undefined;
  return { title, bullets: bullets.length > 0 ? bullets : [""], notes };
}

export function SlideEditor({ deck, onClose }: SlideEditorProps) {
  const { tx, t } = useLocalization();
  const { toast } = useToast();
  const [name, setName] = useState(deck.name);
  const [slides, setSlides] = useState<Slide[]>(
    deck.slides.length > 0
      ? deck.slides.map(normalizeSlide)
      : [{ title: "Untitled Slide", bullets: [""] }]
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const activeSlide = slides[activeIndex];

  const updateActiveSlide = (patch: Partial<Slide>) => {
    setSlides((prev) => prev.map((s, i) => (i === activeIndex ? { ...s, ...patch } : s)));
  };

  const updateBullet = (bulletIndex: number, value: string) => {
    const bullets = [...activeSlide.bullets];
    bullets[bulletIndex] = value;
    updateActiveSlide({ bullets });
  };

  const addBullet = () => {
    updateActiveSlide({ bullets: [...activeSlide.bullets, ""] });
  };

  const removeBullet = (bulletIndex: number) => {
    updateActiveSlide({ bullets: activeSlide.bullets.filter((_, i) => i !== bulletIndex) });
  };

  const addSlide = () => {
    setSlides((prev) => [...prev, { title: "New Slide", bullets: [""] }]);
    setActiveIndex(slides.length);
  };

  const removeSlide = (index: number) => {
    if (slides.length <= 1) return;
    setSlides((prev) => prev.filter((_, i) => i !== index));
    setActiveIndex((prev) => Math.max(0, prev - (index <= prev ? 1 : 0)));
  };

  const handleSave = async () => {
    setIsSaving(true);
    const { error } = await supabase
      .from("presentation_templates")
      .update({ name, slides: slides as unknown as never })
      .eq("id", deck.id);
    setIsSaving(false);
    if (error) {
      toast({ title: t('Save failed'), description: error.message, variant: "destructive" });
    } else {
      toast({ title: t('Saved') });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onClose}>
          <ChevronLeft className="h-4 w-4 me-2" />
          
          {t('Back to Presentations')}
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Save className="h-4 w-4 me-2" />}
            
            {t('Save')}
          </Button>
          <ExportButtons deckName={name} slides={slides} />
        </div>
      </div>

      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="text-lg font-semibold max-w-md"
        placeholder={t('Presentation name')}
      />

      <div className="grid grid-cols-[200px_1fr] gap-4">
        <div className="space-y-2">
          {slides.map((slide, index) => (
            <Card
              key={index}
              className={`cursor-pointer p-2 ${index === activeIndex ? "border-primary" : ""}`}
              onClick={() => setActiveIndex(index)}
            >
              <CardContent className="p-2 flex items-center justify-between">
                <span className="text-xs truncate">{index + 1}. {tx(slide.title) || t('Untitled')}</span>
                {slides.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSlide(index);
                    }}
                    className="text-destructive"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </CardContent>
            </Card>
          ))}
          <Button variant="outline" size="sm" className="w-full" onClick={addSlide}>
            <Plus className="h-4 w-4 me-2" />
            
            {t('Add Slide')}
          </Button>
        </div>

        <Card>
          <CardContent className="p-4 space-y-4">
            <Input
              value={activeSlide.title}
              onChange={(e) => updateActiveSlide({ title: e.target.value })}
              placeholder={t('Slide title')}
              className="text-base font-medium"
            />
            <div className="space-y-2">
              {activeSlide.bullets.map((bullet, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    value={bullet}
                    onChange={(e) => updateBullet(i, e.target.value)}
                    placeholder={t('Bullet point')}
                  />
                  <Button size="icon" variant="ghost" onClick={() => removeBullet(i)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addBullet}>
                <Plus className="h-4 w-4 me-2" />
                
                {t('Add Bullet')}
              </Button>
            </div>
            <Textarea
              value={activeSlide.notes ?? ""}
              onChange={(e) => updateActiveSlide({ notes: e.target.value })}
              placeholder={t('Speaker notes (optional)')}
              rows={3}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
